import React, { useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest } from '../../auth/msalConfig';
import { fetchGraphUser } from '../../auth/fetchGraphUser';
import { UserContext } from '../../contexts/UserContext';
import './Login.css';

import { getAuthConfig, getApiBaseUrl } from '../../services/configService';

const Login = () => {
  const location = useLocation();
  const from = location.state?.from || '/visits';
  const { instance } = useMsal(); 
  const navigate = useNavigate(); 
  const { setUser } = useContext(UserContext);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const simulateDelay = (ms) => new Promise(res => setTimeout(res, ms));

  const handleLogin = async () => {
    setIsLoading(true);
    setErrorMsg('');
    try {
      const authConfig = getAuthConfig();
      if (instance.getAllAccounts().length === 0) {
        await instance.loginPopup(loginRequest);
      }
      const accounts = instance.getAllAccounts();
      const currentAccount = accounts[0];
      if (!currentAccount) throw new Error('No account found after login.');
  
      let tokenResponse;
      try {
        tokenResponse = await instance.acquireTokenSilent({
          scopes: ['User.Read'],
          account: currentAccount,
        });
      } catch (tokenErr) {
        if (tokenErr instanceof InteractionRequiredAuthError) {
          await instance.loginPopup({ scopes: ['User.Read'] });
          tokenResponse = await instance.acquireTokenSilent({
            scopes: ['User.Read'],
            account: instance.getAllAccounts()[0],
          });
        } else {
          throw tokenErr;
        }
      }
  
      const userData = await fetchGraphUser(tokenResponse.accessToken);

      const apiToken = await instance.acquireTokenSilent({
        scopes: [`api://${authConfig.clientId}/access_as_user`],
        account: currentAccount,
      });
      

      const email = (userData.mail || userData.userPrincipalName)
      ?.trim()
      .toLowerCase();
      const encodedEmail = encodeURIComponent(email);
      const url = `${getApiBaseUrl()}/admin/argus-auth/by-email?emails=${encodedEmail}`;  
      let authData = [];

      try {
        const authRes = await fetch(url, {
          headers: {
            Authorization: `Bearer ${apiToken.accessToken}`,
          },
        });

        if (authRes.ok) {
          authData = await authRes.json();
        } else {
          console.warn('Auth API failed — defaulting to no roles');
        }
      
      } catch {
        console.warn('Auth API error — defaulting to no roles');
      }
      
      const roles = {
        is_admin: false,
        is_ffc: false,
        is_leadership: false,
      };
      
      let revenueIds = [];
      
      authData.forEach(r => {
        if (r.is_admin) roles.is_admin = true;
        if (r.is_ffc) roles.is_ffc = true;
        if (r.is_leadership) roles.is_leadership = true;
      
        if (r.revenue_id) {
          revenueIds.push(
            ...r.revenue_id
              .split(',')
              .map(x => x.trim().toUpperCase())
          );
        }
      });
      
      revenueIds = [...new Set(revenueIds)];
      
      const fullUser = {
        ...userData,
        roles,
        revenueIds
      };
      
      setUser(fullUser);
      sessionStorage.setItem("userData", JSON.stringify(fullUser));
      
      await simulateDelay(1200);
  
      navigate(from, { replace: true });
    } catch (err) {
      setErrorMsg('Something went wrong, please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-container split-layout">

      <aside className="info-panel">

        <div className="top-content">
          <img
            src="/bluepearl-logo-white.png"
            alt="BluePearl Logo"
            className="logo-img"
            loading="lazy"
            width={160}
            height={80}
          />
          <h1 className="header-title">Welcome to ARGUS!</h1>
          <p className="about-text">
            ARGUS Front-End is a secure, innovative platform designed to enhance hospital invoice auditing and provide real-time charge tracking. It seamlessly integrates with your existing practice management system and leverages Microsoft Graph authentication to protect your data with secure, role-based access.
          </p>
          <p className="about-text subtle">
            Need help with ARGUS?{' '}
            <a
              href="https://marsvh.service-now.com/bluepearl_it?id=sc_cat_item&sys_id=c4dfdde11b82219056b0bb7f034bcb3d"
              className="support-link"
              style={{ color: 'white', textDecoration: 'underline' }}
            >
              Contact Support
            </a>
            {' '}anytime.
          </p>
        </div>

        <section className="whats-new-box">
          <h2 className="whats-new-title">What’s New in ARGUS</h2>
          <ul className="whats-new-list">
            <li><strong>v2.0.5:</strong> Introduced a redesigned Journal Entry workflow with a modern, streamlined modal for reviewing invoice line items.</li>
            <li><strong>Journal Entry Modal:</strong> Replaced legacy inline dialogs with a reusable, self-contained modal that handles validation, submission, and error handling internally.</li>
            <li><strong>Improved UI & Layout:</strong> Updated the Journal Entry form with a cleaner, professional design—grouping read-only fields separately from editable fields for better clarity.</li>
            <li><strong>Enhanced Form Controls:</strong> Added compact, pill-style action toggles (Escalate, Price Issue, Placeholder, No Error) with clear visual feedback.</li>
            <li><strong>User-Friendly Formatting:</strong> Improved date readability across the app, including human-readable timestamps in modals and detail views.</li>
            <li><strong>Improved Medical Note Search:</strong> Restored and enhanced medical note search functionality with contextual snippet previews and quick access to highlighted terms.</li>
            <li><strong>Smarter Highlighting:</strong> Enabled clickable tags in medical notes that scroll and cycle through relevant highlighted text segments.</li>
            <li><strong>Invoice Table Enhancements:</strong> Improved edit interactions and stabilized row-level actions for missed, voided, and captured charges.</li>
            <li><strong>UX Polish:</strong> Standardized input and dropdown heights, reduced visual clutter, and aligned typography for a more consistent experience.</li>
            <li><strong>Stability Fixes:</strong> Resolved regressions introduced during refactors, including restored note search results and reliable modal triggering.</li>
          </ul>
        </section>

      </aside>

      <main className="login-panel enhanced-login glass-card">
        <header className="login-header">
          <img src="/bluepearl-logo.png" alt="BluePearl Logo Login" className="login-bp-logo" />
          <h2 className="login-title">Ready to explore ARGUS?</h2>
        </header>

        {isLoading ? (
          <div className="loading-indicator">
            <div className="spinner" />
            <span className="loading-text">Logging you in...</span>
          </div>
        ) : (
          <button
            onClick={handleLogin}
            className="login-button"
            aria-label="Sign in with Microsoft"
          >
            <svg
              className="login-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <rect x="1" y="1" width="10" height="10" fill="#F25022" /> 
              <rect x="13" y="1" width="10" height="10" fill="#7FBA00" /> 
              <rect x="1" y="13" width="10" height="10" fill="#00A4EF" /> 
              <rect x="13" y="13" width="10" height="10" fill="#FFB900" /> 
            </svg>
            <span className="btn-text">Sign in with Microsoft</span>
          </button>
        )}

        {errorMsg && (
          <div className="error-message" role="alert" aria-live="assertive">
            {errorMsg}
          </div>
        )}

        <p className="info-text">
          {isLoading ? 'A secure sign-in has opened in a new window.' : ' '}
        </p>
        <div className="privacy-row">
          <small>
            Sign in with Microsoft SSO — your credentials are <b>never stored</b>.
          </small>
        </div>
      </main>
    </div>
  );
};

export default Login;